import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    X, Plus, Trash2, Save, AlertCircle, CheckCircle, Loader2, Clock,
    DollarSign, Percent, Shield, Settings, Calendar, ArrowRight
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import UserDropdown from "../../components/ui/UserDropdown";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
    createPaymentMilestone,
    updatePaymentMilestone,
    createPaymentMilestoneStage,
    updatePaymentMilestoneStage,
    deletePaymentMilestoneStage,
    clearPaymentMilestonesError,
    clearPaymentMilestoneStagesError
} from "../../redux/Slices/PaymentMilestoneSlice";
import { 
    PaymentMilestoneStatusEnum, 
    PaymentMilestoneCategoryTypeEnum, 
    PaymentMilestoneStageReleaseConditionEnum,
    DisputePeriodEnum,
    PaymentMilestoneUserRef,
    RoleEnum
} from "../../modules/shared/model";

interface PaymentMilestoneModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    editMilestone?: any;
}

interface MilestoneStage {
    id?: number;
    name: string;
    percentage: number;
    releaseCondition: string[];
    holdingPeriod: number;
    refundable: boolean;
}

const PaymentMilestoneModal: React.FC<PaymentMilestoneModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    editMilestone
}) => {
    const dispatch = useAppDispatch();

    // Redux state
    const { milestonesLoading, milestonesError, stagesLoading, stagesError } = useAppSelector(
        (state) => state.paymentMilestones
    );

    // Form state
    const [formData, setFormData] = useState({
        name: "",
        status: { id: PaymentMilestoneStatusEnum.Active, name: "Active" },
        categoryType: undefined as { id: PaymentMilestoneCategoryTypeEnum; name: string } | undefined,
        disputePeriodSetting: "7 days",
        escrowEnabledSetting: true,
        clientApprovalSetting: true,
        autoReleaseSetting: true,
        user: undefined as PaymentMilestoneUserRef | undefined
    });

    const [stages, setStages] = useState<MilestoneStage[]>([
        {
            name: "",
            percentage: 0,
            releaseCondition: [""],
            holdingPeriod: 24,
            refundable: true
        }
    ]);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [existingStages, setExistingStages] = useState<MilestoneStage[]>([]);
    const [deletingStages, setDeletingStages] = useState(false);

    // Clear errors when modal opens
    useEffect(() => {
        if (isOpen) {
            dispatch(clearPaymentMilestonesError());
            dispatch(clearPaymentMilestoneStagesError());
            setErrors({});
        }
    }, [isOpen, dispatch]);

    // Load existing milestone data when editing
    useEffect(() => {
        if (isOpen && editMilestone) {
            setFormData({
                name: editMilestone.name || "",
                status: editMilestone.status ? { id: Number(editMilestone.status.id), name: editMilestone.status.name } : { id: PaymentMilestoneStatusEnum.Active, name: "Active" },
                categoryType: editMilestone.categoryType ? { id: Number(editMilestone.categoryType.id), name: editMilestone.categoryType.name } : undefined,
                disputePeriodSetting: editMilestone.disputePeriodSetting || "7 days",
                escrowEnabledSetting: editMilestone.escrowEnabledSetting ?? true,
                clientApprovalSetting: editMilestone.clientApprovalSetting ?? true,
                autoReleaseSetting: editMilestone.autoReleaseSetting ?? true,
                user: editMilestone.user ? {
                    id: editMilestone.user.id,
                    firstName: editMilestone.user.firstName,
                    lastName: editMilestone.user.lastName,
                    email: editMilestone.user.email
                } : undefined
            });

            // Use stages from the milestone data
            if (editMilestone.stages && editMilestone.stages.length > 0) {
                setExistingStages(editMilestone.stages);
                setStages(editMilestone.stages.map(stage => ({
                    id: stage.id,
                    name: stage.name,
                    percentage: stage.percentage,
                    releaseCondition: Array.isArray(stage.releaseCondition) ? stage.releaseCondition : [stage.releaseCondition || ""],
                    holdingPeriod: stage.holdingPeriod,
                    refundable: stage.refundable
                })));
            }
        } else if (isOpen && !editMilestone) {
            // Reset form for new milestone
            setFormData({
                name: "",
                status: { id: PaymentMilestoneStatusEnum.Active, name: "Active" },
                categoryType: undefined,
                disputePeriodSetting: "7 days",
                escrowEnabledSetting: true,
                clientApprovalSetting: true,
                autoReleaseSetting: true,
                user: undefined
            });
            setStages([{
                name: "",
                percentage: 0,
                releaseCondition: [""],
                holdingPeriod: 24,
                refundable: true
            }]);
            setExistingStages([]);
        }
    }, [isOpen, editMilestone]);

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = "Milestone name is required";
        }

        if (stages.length === 0) {
            newErrors.stages = "At least one stage is required";
        }

        stages.forEach((stage, index) => {
            if (!stage.name.trim()) {
                newErrors[`stage_${index}_name`] = "Stage name is required";
            }
            if (stage.percentage <= 0 || stage.percentage > 100) {
                newErrors[`stage_${index}_percentage`] = "Percentage must be between 1 and 100";
            }
            if (!stage.releaseCondition || stage.releaseCondition.length === 0 || stage.releaseCondition.every(condition => !condition.trim())) {
                newErrors[`stage_${index}_releaseCondition`] = "At least one release condition is required";
            }
            if (stage.holdingPeriod <= 0) {
                newErrors[`stage_${index}_holdingPeriod`] = "Holding period must be greater than 0";
            }
        });

        const totalPercentage = stages.reduce((sum, stage) => sum + stage.percentage, 0);
        if (totalPercentage !== 100) {
            newErrors.totalPercentage = "Total percentage must equal 100%";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            if (editMilestone) {
                // Update existing milestone
                
                await dispatch(updatePaymentMilestone({
                    id: editMilestone.id,
                    data: formData
                })).unwrap();

                // Handle stages
                const stagesToCreate = stages.filter(stage => !stage.id);
                const stagesToUpdate = stages.filter(stage => stage.id);

                // Create new stages
                for (const stage of stagesToCreate) {
                    await dispatch(createPaymentMilestoneStage({
                        paymentMilestone: { id: editMilestone.id, name: editMilestone.name },
                        ...stage
                    })).unwrap();
                }

                // Update existing stages
                for (const stage of stagesToUpdate) {
                    await dispatch(updatePaymentMilestoneStage({
                        id: stage.id!,
                        data: stage
                    })).unwrap();
                }

                // Delete removed stages
                const stagesToDelete = existingStages.filter(existingStage => 
                    !stages.some(stage => stage.id === existingStage.id)
                );

                for (const stage of stagesToDelete) {
                    if (stage.id) {
                        await dispatch(deletePaymentMilestoneStage(stage.id)).unwrap();
                    }
                }
            } else {
                // Create new milestone
                const newMilestone = await dispatch(createPaymentMilestone(formData)).unwrap();

                // Create stages
                for (const stage of stages) {
                    await dispatch(createPaymentMilestoneStage({
                        paymentMilestone: { id: newMilestone.id, name: newMilestone.name },
                        ...stage
                    })).unwrap();
                }
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Error saving payment milestone:", error);
        }
    };

    const addStage = () => {
        setStages([...stages, {
            name: "",
            percentage: 0,
            releaseCondition: [""],
            holdingPeriod: 24,
            refundable: true
        }]);
    };

    const removeStage = (index: number) => {
        const newStages = stages.filter((_, i) => i !== index);
        setStages(newStages);
    };

    const updateStage = (index: number, field: keyof MilestoneStage, value: any) => {
        const newStages = [...stages];
        newStages[index] = { ...newStages[index], [field]: value };
        setStages(newStages);
    };

    const addReleaseCondition = (stageIndex: number) => {
        const newStages = [...stages];
        newStages[stageIndex].releaseCondition.push("");
        setStages(newStages);
    };

    const removeReleaseCondition = (stageIndex: number, conditionIndex: number) => {
        const newStages = [...stages];
        newStages[stageIndex].releaseCondition = newStages[stageIndex].releaseCondition.filter((_, i) => i !== conditionIndex);
        setStages(newStages);
    };

    const updateReleaseCondition = (stageIndex: number, conditionIndex: number, value: string) => {
        const newStages = [...stages];
        newStages[stageIndex].releaseCondition[conditionIndex] = value;
        setStages(newStages);
    };

    const getStatusName = (status: PaymentMilestoneStatusEnum) => {
        switch (status) {
            case PaymentMilestoneStatusEnum.Active: return "Active";
            case PaymentMilestoneStatusEnum.Inactive: return "Inactive";
            case PaymentMilestoneStatusEnum.Draft: return "Draft";
            default: return "Active";
        }
    };

    const getCategoryName = (category: PaymentMilestoneCategoryTypeEnum) => {
        switch (category) {
            case PaymentMilestoneCategoryTypeEnum.LocalServices: return "Local Services";
            case PaymentMilestoneCategoryTypeEnum.Professional: return "Professional";
            case PaymentMilestoneCategoryTypeEnum.International: return "International";
            case PaymentMilestoneCategoryTypeEnum.Emergency: return "Emergency";
            default: return "Local Services";
        }
    };

    const getDisputePeriodOptions = () => {
        return Object.entries(DisputePeriodEnum).filter(([key, value]) => typeof value === "number");
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
                <div className="p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-2xl font-bold">
                            {editMilestone ? "Edit Payment Milestone" : "Create Payment Milestone"}
                        </h2>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Basic Information */}
                        <Card className="p-6">
                            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Milestone Name</label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Enter milestone name"
                                        className={errors.name ? "border-red-500" : ""}
                                    />
                                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Status</label>
                                    <select
                                        value={formData.status.id}
                                        onChange={(e) => {
                                            const statusId = Number(e.target.value);
                                            const statusName = e.target.selectedOptions[0].text;
                                            setFormData({ ...formData, status: { id: statusId, name: statusName } });
                                        }}
                                        className="w-full p-2 border rounded-lg"
                                    >
                                        <option value={PaymentMilestoneStatusEnum.Active}>Active</option>
                                        <option value={PaymentMilestoneStatusEnum.Inactive}>Inactive</option>
                                        <option value={PaymentMilestoneStatusEnum.Draft}>Draft</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Category Type (Optional)</label>
                                    <select
                                        value={formData.categoryType?.id || ""}
                                        onChange={(e) => {
                                            if (e.target.value) {
                                                const categoryId = Number(e.target.value);
                                                const categoryName = e.target.selectedOptions[0].text;
                                                setFormData({ ...formData, categoryType: { id: categoryId, name: categoryName } });
                                            } else {
                                                setFormData({ ...formData, categoryType: undefined });
                                            }
                                        }}
                                        className="w-full p-2 border rounded-lg"
                                    >
                                        <option value="">Select a category (optional)</option>
                                        <option value={PaymentMilestoneCategoryTypeEnum.LocalServices}>Local Services</option>
                                        <option value={PaymentMilestoneCategoryTypeEnum.Professional}>Professional</option>
                                        <option value={PaymentMilestoneCategoryTypeEnum.International}>International</option>
                                        <option value={PaymentMilestoneCategoryTypeEnum.Emergency}>Emergency</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Dispute Period</label>
                                    <select
                                        value={formData.disputePeriodSetting}
                                        onChange={(e) => setFormData({ ...formData, disputePeriodSetting: e.target.value })}
                                        className="w-full p-2 border rounded-lg"
                                    >
                                        {getDisputePeriodOptions().map(([key, value]) => (
                                            <option key={value} value={key}>{key}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">User (Optional)</label>
                                    <UserDropdown
                                        selectedUser={formData.user ? {
                                            id: formData.user.id,
                                            firstName: formData.user.firstName || "",
                                            lastName: formData.user.lastName || "",
                                            email: formData.user.email || "",
                                        } : null}
                                        onUserSelect={(user) => setFormData({ 
                                            ...formData, 
                                            user: user ? { 
                                                id: user.id,
                                                firstName: user.firstName,
                                                lastName: user.lastName,
                                                email: user.email
                                            } as PaymentMilestoneUserRef : undefined 
                                        })}
                                        placeholder="Select a user (optional)"
                                        className="w-full"
                                        roles={[
                                            { id: RoleEnum.professional },
                                            { id: RoleEnum.internationl_professional }
                                        ]}
                                    />
                                </div>
                            </div>
                        </Card>

                        {/* Settings */}
                        <Card className="p-6">
                            <h3 className="text-lg font-semibold mb-4">Settings</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="escrowEnabled"
                                        checked={formData.escrowEnabledSetting}
                                        onChange={(e) => setFormData({ ...formData, escrowEnabledSetting: e.target.checked })}
                                        className="rounded"
                                    />
                                    <label htmlFor="escrowEnabled" className="text-sm font-medium">Escrow Enabled</label>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="clientApproval"
                                        checked={formData.clientApprovalSetting}
                                        onChange={(e) => setFormData({ ...formData, clientApprovalSetting: e.target.checked })}
                                        className="rounded"
                                    />
                                    <label htmlFor="clientApproval" className="text-sm font-medium">Client Approval Required</label>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="autoRelease"
                                        checked={formData.autoReleaseSetting}
                                        onChange={(e) => setFormData({ ...formData, autoReleaseSetting: e.target.checked })}
                                        className="rounded"
                                    />
                                    <label htmlFor="autoRelease" className="text-sm font-medium">Auto Release</label>
                                </div>
                            </div>
                        </Card>

                        {/* Payment Stages */}
                        <Card className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold">Payment Stages</h3>
                                <Button
                                    type="button"
                                    onClick={addStage}
                                    className="flex items-center gap-2"
                                    size="sm"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Stage
                                </Button>
                            </div>

                            {errors.stages && <p className="text-red-500 text-sm mb-4">{errors.stages}</p>}
                            {errors.totalPercentage && <p className="text-red-500 text-sm mb-4">{errors.totalPercentage}</p>}

                            <div className="space-y-4">
                                {stages.map((stage, index) => (
                                    <div key={index} className="border rounded-lg p-4">
                                        <div className="flex justify-between items-center mb-4">
                                            <h4 className="font-medium">Stage {index + 1}</h4>
                                            {stages.length > 1 && (
                                                <Button
                                                    type="button"
                                                    onClick={() => removeStage(index)}
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-red-600 hover:text-red-700"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium mb-2">Stage Name</label>
                                                <Input
                                                    value={stage.name}
                                                    onChange={(e) => updateStage(index, "name", e.target.value)}
                                                    placeholder="Enter stage name"
                                                    className={errors[`stage_${index}_name`] ? "border-red-500" : ""}
                                                />
                                                {errors[`stage_${index}_name`] && (
                                                    <p className="text-red-500 text-sm mt-1">{errors[`stage_${index}_name`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-2">Percentage (%)</label>
                                                <Input
                                                    type="number"
                                                    value={stage.percentage}
                                                    onChange={(e) => updateStage(index, "percentage", Number(e.target.value))}
                                                    placeholder="Enter percentage"
                                                    min="0"
                                                    max="100"
                                                    className={errors[`stage_${index}_percentage`] ? "border-red-500" : ""}
                                                />
                                                {errors[`stage_${index}_percentage`] && (
                                                    <p className="text-red-500 text-sm mt-1">{errors[`stage_${index}_percentage`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-2">Release Conditions</label>
                                                <div className="space-y-2">
                                                    {stage.releaseCondition.map((condition, conditionIndex) => (
                                                        <div key={conditionIndex} className="flex items-center gap-2">
                                                            <Input
                                                                value={condition}
                                                                onChange={(e) => updateReleaseCondition(index, conditionIndex, e.target.value)}
                                                                placeholder="Enter release condition"
                                                                className={errors[`stage_${index}_releaseCondition`] ? "border-red-500" : ""}
                                                            />
                                                            {stage.releaseCondition.length > 1 && (
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => removeReleaseCondition(index, conditionIndex)}
                                                                    className="text-red-600 hover:text-red-700"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => addReleaseCondition(index)}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <Plus className="h-4 w-4" />
                                                        Add Condition
                                                    </Button>
                                                </div>
                                                {errors[`stage_${index}_releaseCondition`] && (
                                                    <p className="text-red-500 text-sm mt-1">{errors[`stage_${index}_releaseCondition`]}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium mb-2">Holding Period (hours)</label>
                                                <Input
                                                    type="number"
                                                    value={stage.holdingPeriod}
                                                    onChange={(e) => updateStage(index, "holdingPeriod", Number(e.target.value))}
                                                    placeholder="Enter holding period"
                                                    min="1"
                                                    className={errors[`stage_${index}_holdingPeriod`] ? "border-red-500" : ""}
                                                />
                                                {errors[`stage_${index}_holdingPeriod`] && (
                                                    <p className="text-red-500 text-sm mt-1">{errors[`stage_${index}_holdingPeriod`]}</p>
                                                )}
                                            </div>

                                            <div className="md:col-span-2">
                                                <div className="flex items-center space-x-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`refundable_${index}`}
                                                        checked={stage.refundable}
                                                        onChange={(e) => updateStage(index, "refundable", e.target.checked)}
                                                        className="rounded"
                                                    />
                                                    <label htmlFor={`refundable_${index}`} className="text-sm font-medium">
                                                        Refundable
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {/* Error Display */}
                        {(milestonesError || stagesError) && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <div className="flex items-center">
                                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                                    <p className="text-red-700">
                                        {milestonesError || stagesError}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={milestonesLoading || stagesLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={milestonesLoading || stagesLoading}
                                className="flex items-center gap-2"
                            >
                                {(milestonesLoading || stagesLoading) ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4" />
                                )}
                                {editMilestone ? "Update Milestone" : "Create Milestone"}
                            </Button>
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};

export default PaymentMilestoneModal;
